import type { FC } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { emptyUrl } from '@/constants/casinoGame';
import type { FullscreenContextProps, FullscreenProviderProps, WithWebkit } from './CasinoGamePage.types';
import { isFullscreenSupports } from './CasinoGamePage.utils';
import styles from './CasinoGamePage.module.scss';
const Context = createContext<FullscreenContextProps>(({} as FullscreenContextProps));
export const useFullscreen = (): FullscreenContextProps => useContext(Context);
const useProvideFullscreen = (url: string): FullscreenContextProps => {
  const [isFullscreen, setFullscreen] = useState<boolean>(false);
  const [notSupported, setNotSupported] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<WithWebkit<HTMLDivElement>>(null);
  const onChangeFullscreen = useCallback((isOpen: boolean) => setFullscreen(isOpen), []);
  const handleFullscreenChange = useCallback(() => {
    setFullscreen(prevFullScreen => !prevFullScreen);
  }, []);
  const onRequestExitFullscreen = useCallback(() => {
    if (!isFullscreenSupports()) {
      document.body.classList.remove(styles.bodyFullscreen);
      setFullscreen(false);
      setNotSupported(true);
      return;
    }
    if (document.exitFullscreen && document.fullscreenElement) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen && document.webkitFullscreenElement) {
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen && document.mozFullScreenElement) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen && document.msFullscreenElement) {
      document.msExitFullscreen();
    }
  }, []);
  const onRequestFullscreen = useCallback(() => {
    const containerFrame = containerRef.current;
    if (!containerFrame) {
      return;
    }
    if (!isFullscreenSupports()) {
      document.body.classList.add(styles.bodyFullscreen);
      setFullscreen(true);
      setNotSupported(true);
      return;
    }
    if (containerFrame.webkitRequestFullscreen) {
      containerFrame.webkitRequestFullscreen();
    } else if (containerFrame.mozRequestFullScreen) {
      containerFrame.mozRequestFullScreen();
    } else if (containerFrame.msRequestFullscreen) {
      containerFrame.msRequestFullscreen();
    } else {
      containerFrame.requestFullscreen().catch(() => undefined);
    }
  }, []);
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange, false);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange, false);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange, false);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange, false);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange, false);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange, false);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange, false);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange, false);
    };
  }, [handleFullscreenChange]);
  useEffect(() => {
    const frameEl = frameRef.current;
    if (url && frameEl?.contentWindow) {
      frameEl.contentWindow.location.replace(url);
    }
    return () => {
      document.body.classList.remove(styles.bodyFullscreen);
    };
  }, [onRequestFullscreen, url]);
  return {
    isFullscreen,
    notSupported,
    frameRef,
    containerRef,
    url,
    onChangeFullscreen,
    onRequestFullscreen,
    onRequestExitFullscreen
  };
};
export const FullscreenProvider: FC<FullscreenProviderProps> = ({
  children,
  url = emptyUrl
}) => {
  const value = useProvideFullscreen(url);
  return <Context.Provider value={value} data-sentry-element="unknown" data-sentry-component="FullscreenProvider" data-sentry-source-file="CasinoGamePage.context.tsx">{children}</Context.Provider>;
};